import React, { useEffect, useMemo, useRef, useState } from "react";
import { Headset, Send, Loader2, Inbox, CircleDot, Clock, User, Volume2, VolumeX, Zap, Coffee, FileText, Download, Paperclip } from "lucide-react";
import { renderMarkdown, formatDateTime } from "@/lib/format";
import { API_BASE } from "@/lib/api";
import { playChime } from "@/lib/chime";
import { toast } from "sonner";

function ConsoleAttachment({ attachment, light }) {
    const url = API_BASE + attachment.url;
    if (attachment.is_image) {
        return (
            <a href={url} target="_blank" rel="noreferrer" data-testid="console-attachment-image">
                <img src={url} alt={attachment.filename} className="rounded-lg max-h-56 max-w-full border border-white/20 mt-0.5" />
            </a>
        );
    }
    return (
        <a
            href={url}
            target="_blank"
            rel="noreferrer"
            data-testid="console-attachment-file"
            className={`inline-flex items-center gap-2 hover:underline mt-0.5 ${light ? "text-white" : "text-orchid-700"}`}
        >
            <FileText size={16} /> {attachment.filename} <Download size={13} />
        </a>
    );
}

function StatusPill({ status }) {
    const map = {
        requested: { label: "Waiting", cls: "bg-gold-500/15 text-gold-700" },
        assigned: { label: "Assigned", cls: "bg-orchid-700/12 text-orchid-700" },
        active: { label: "Live", cls: "bg-sage-600/15 text-sage-700" },
        ended: { label: "Ended", cls: "bg-stone-200 text-stone-500" },
    };
    const s = map[status] || map.ended;
    return <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${s.cls}`}>{s.label}</span>;
}

export default function LiveChatConsole({ api }) {
    const [lists, setLists] = useState({ queue: [], mine: [], others: [], pending_count: 0 });
    const [selected, setSelected] = useState(null);
    const [detail, setDetail] = useState(null);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadPct, setUploadPct] = useState(0);
    const [loadingList, setLoadingList] = useState(true);
    const [soundOn, setSoundOn] = useState(true);
    const [identity, setIdentity] = useState(null);
    const [availability, setAvailability] = useState("available");
    const [canned, setCanned] = useState([]);
    const [showCanned, setShowCanned] = useState(false);
    const prevPending = useRef(0);
    const detailEndRef = useRef(null);
    const selectedRef = useRef(null);
    selectedRef.current = selected;
    const fileInputRef = useRef(null);

    // Load identity (role + availability) and canned replies once.
    useEffect(() => {
        api.get("/api/agent/me")
            .then((r) => {
                setIdentity(r.data);
                if (r.data?.availability) setAvailability(r.data.availability);
            })
            .catch(() => {});
        api.get("/api/live/canned-replies")
            .then((r) => setCanned(r.data?.items || []))
            .catch(() => {});
    }, [api]);

    const toggleAvailability = async () => {
        const next = availability === "available" ? "away" : "available";
        setAvailability(next);
        try {
            await api.put("/api/agent/availability", { status: next });
            toast.success(next === "available" ? "You're now available for new chats" : "You're away — new chats won't auto-route to you");
        } catch (e) {
            setAvailability(availability); // revert
            toast.error("Could not update availability");
        }
    };

    const insertCanned = (text) => {
        setInput((prev) => (prev ? `${prev} ${text}` : text));
        setShowCanned(false);
    };

    // Heartbeat (marks an agent online) + list polling
    useEffect(() => {
        let stopped = false;
        const tick = async () => {
            try {
                api.post("/api/agent/heartbeat").catch(() => {});
                const { data } = await api.get("/api/live/chats");
                if (stopped) return;
                setLists(data);
                if (data.pending_count > prevPending.current && soundOn) playChime();
                prevPending.current = data.pending_count;
            } catch (e) {
                /* transient */
            } finally {
                setLoadingList(false);
            }
        };
        tick();
        const id = setInterval(tick, 3000);
        return () => {
            stopped = true;
            clearInterval(id);
        };
    }, [api, soundOn]);

    // Poll the open conversation for new guest messages
    useEffect(() => {
        if (!selected) {
            setDetail(null);
            return;
        }
        let stopped = false;
        const tick = async () => {
            try {
                const { data } = await api.get(`/api/live/chats/${selected}`);
                if (!stopped && selectedRef.current === selected) setDetail(data);
            } catch (e) {
                /* transient */
            }
        };
        tick();
        const id = setInterval(tick, 2500);
        return () => {
            stopped = true;
            clearInterval(id);
        };
    }, [selected, api]);

    useEffect(() => {
        detailEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [detail?.messages?.length]);

    const claim = async (sid) => {
        try {
            await api.post(`/api/live/chats/${sid}/claim`);
            setSelected(sid);
            toast.success("Chat claimed — you're now handling it");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not claim chat");
        }
    };

    const sendMsg = async () => {
        const text = input.trim();
        if (!text || !selected || sending) return;
        setSending(true);
        try {
            await api.post(`/api/live/chats/${selected}/message`, { text });
            setInput("");
            const { data } = await api.get(`/api/live/chats/${selected}`);
            setDetail(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Message failed");
        } finally {
            setSending(false);
        }
    };

    const onPickAgentFile = () => {
        if (canType && !uploading) fileInputRef.current?.click();
    };

    const uploadAgentFile = async (file) => {
        if (!file || !selected || uploading) return;
        const ext = (file.name.split(".").pop() || "").toLowerCase();
        if (!["jpg", "jpeg", "png", "webp", "pdf", "doc", "docx"].includes(ext)) {
            toast.error("You can attach images (JPG, PNG, WEBP) or documents (PDF, DOC, DOCX).");
            return;
        }
        if (file.size > 250 * 1024 * 1024) {
            toast.error("File too large. The limit is 250 MB.");
            return;
        }
        const CHUNK = 5 * 1024 * 1024;
        setUploading(true);
        setUploadPct(0);
        try {
            const init = await api.post("/api/chat/upload/init", {
                session_id: selected,
                filename: file.name,
                size: file.size,
            });
            const uploadId = init.data.upload_id;
            const total = Math.max(1, Math.ceil(file.size / CHUNK));
            for (let i = 0; i < total; i++) {
                const blob = file.slice(i * CHUNK, (i + 1) * CHUNK);
                const fd = new FormData();
                fd.append("upload_id", uploadId);
                fd.append("chunk_index", String(i));
                fd.append("chunk", blob, file.name);
                let ok = false;
                let lastErr = null;
                for (let a = 0; a < 3 && !ok; a++) {
                    try {
                        await api.post("/api/chat/upload/chunk", fd, { headers: { "Content-Type": "multipart/form-data" } });
                        ok = true;
                    } catch (err) {
                        lastErr = err;
                        if (err?.response?.status && err.response.status < 500) throw err;
                    }
                }
                if (!ok) throw lastErr || new Error("chunk failed");
                setUploadPct(Math.round(((i + 1) / total) * 100));
            }
            await api.post(`/api/live/chats/${selected}/attachment`, { upload_id: uploadId });
            const { data } = await api.get(`/api/live/chats/${selected}`);
            setDetail(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not send the file. Please try again.");
        } finally {
            setUploading(false);
            setUploadPct(0);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const endChat = async () => {
        if (!selected) return;
        try {
            await api.post(`/api/live/chats/${selected}/end`);
            toast.success("Chat ended — Priya has resumed");
            const { data } = await api.get(`/api/live/chats/${selected}`);
            setDetail(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not end chat");
        }
    };

    const ChatRow = ({ c, showClaim }) => (
        <button
            onClick={() => (showClaim ? claim(c.session_id) : setSelected(c.session_id))}
            data-testid={`live-chat-row-${c.session_id}`}
            className={`w-full text-left px-3.5 py-3 rounded-lg border transition-colors ${
                selected === c.session_id
                    ? "border-orchid-700 bg-orchid-700/5"
                    : "border-stone-200 bg-white hover:border-orchid-700/40"
            }`}
        >
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                    <User size={14} className="text-stone-400 shrink-0" />
                    <span className="text-sm font-medium text-stone-800 truncate">
                        {c.visitor?.name || `Guest ${c.session_id.slice(0, 6)}`}
                    </span>
                </div>
                <StatusPill status={c.status} />
            </div>
            <div className="text-xs text-stone-500 mt-1 truncate">{c.preview || "New request"}</div>
            <div className="flex items-center gap-1 text-[10px] text-stone-400 mt-1">
                <Clock size={10} /> {formatDateTime(c.requested_at || c.last_message_at)}
            </div>
            {showClaim && (
                <span className="inline-block mt-2 text-[11px] font-semibold text-white bg-sage-600 px-2.5 py-1 rounded-full">
                    Claim &amp; respond
                </span>
            )}
        </button>
    );

    const canType = detail && detail.handoff?.status && detail.handoff.status !== "ended";

    return (
        <div className="flex h-full min-h-0 bg-ivory" data-testid="live-chat-console">
            {/* Left: chat lists */}
            <div className="w-80 shrink-0 border-r border-stone-200 bg-white flex flex-col">
                <div className="px-4 py-3.5 border-b border-stone-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Headset size={17} className="text-orchid-700" />
                        <span className="font-semibold text-stone-900">Live Chats</span>
                        {lists.pending_count > 0 && (
                            <span className="text-[11px] font-bold text-white bg-sage-600 rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center" data-testid="pending-badge">
                                {lists.pending_count}
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        {identity?.role === "agent" && (
                            <button
                                onClick={toggleAvailability}
                                data-testid="availability-toggle"
                                title="Toggle your availability"
                                className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full transition-colors ${
                                    availability === "available"
                                        ? "bg-sage-600/15 text-sage-700 hover:bg-sage-600/25"
                                        : "bg-gold-500/15 text-gold-700 hover:bg-gold-500/25"
                                }`}
                            >
                                {availability === "available" ? <CircleDot size={12} /> : <Coffee size={12} />}
                                {availability === "available" ? "Available" : "Away"}
                            </button>
                        )}
                        <button
                            onClick={() => setSoundOn((s) => !s)}
                            title={soundOn ? "Mute notifications" : "Unmute notifications"}
                            data-testid="toggle-sound"
                            className="text-stone-400 hover:text-orchid-700"
                        >
                            {soundOn ? <Volume2 size={16} /> : <VolumeX size={16} />}
                        </button>
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto scroll-soft p-3 space-y-4">
                    {loadingList ? (
                        <div className="flex items-center gap-2 text-sm text-stone-400 p-4">
                            <Loader2 size={14} className="animate-spin" /> Loading…
                        </div>
                    ) : lists.queue.length === 0 && lists.mine.length === 0 && lists.others.length === 0 ? (
                        <div className="text-center text-stone-400 py-12 px-4" data-testid="live-empty-state">
                            <Inbox size={34} strokeWidth={1.2} className="mx-auto mb-3" />
                            <p className="text-sm font-medium text-stone-500">No active live chats</p>
                            <p className="text-xs mt-1">New guest requests will appear here with a sound alert.</p>
                        </div>
                    ) : (
                        <>
                            {lists.queue.length > 0 && (
                                <div>
                                    <div className="text-[11px] uppercase tracking-wider text-gold-700 font-semibold mb-2 px-1">
                                        Waiting queue ({lists.queue.length})
                                    </div>
                                    <div className="space-y-2">
                                        {lists.queue.map((c) => (
                                            <ChatRow key={c.session_id} c={c} showClaim />
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div>
                                <div className="text-[11px] uppercase tracking-wider text-orchid-700 font-semibold mb-2 px-1">
                                    My chats ({lists.mine.length})
                                </div>
                                {lists.mine.length === 0 ? (
                                    <div className="text-xs text-stone-400 px-1">No active chats assigned to you.</div>
                                ) : (
                                    <div className="space-y-2">
                                        {lists.mine.map((c) => (
                                            <ChatRow key={c.session_id} c={c} />
                                        ))}
                                    </div>
                                )}
                            </div>
                            {lists.others.length > 0 && (
                                <div>
                                    <div className="text-[11px] uppercase tracking-wider text-stone-400 font-semibold mb-2 px-1">
                                        Other agents ({lists.others.length})
                                    </div>
                                    <div className="space-y-2 opacity-70">
                                        {lists.others.map((c) => (
                                            <ChatRow key={c.session_id} c={c} />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Right: conversation */}
            <div className="flex-1 flex flex-col min-w-0">
                {!selected ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-stone-400 gap-3">
                        <Inbox size={40} strokeWidth={1.2} />
                        <p className="text-sm">Select a chat to start responding.</p>
                    </div>
                ) : (
                    <>
                        <div className="px-5 py-3.5 border-b border-stone-200 bg-white flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <CircleDot size={16} className="text-sage-600" />
                                <span className="font-medium text-stone-900">
                                    {detail?.visitor?.name || `Guest ${selected.slice(0, 6)}`}
                                </span>
                                {detail?.handoff?.status && <StatusPill status={detail.handoff.status} />}
                            </div>
                            {canType && (
                                <button
                                    onClick={endChat}
                                    data-testid="end-chat-btn"
                                    className="text-xs font-semibold text-white bg-stone-800 hover:bg-stone-700 px-3.5 py-1.5 rounded-md"
                                >
                                    End chat
                                </button>
                            )}
                        </div>

                        <div className="flex-1 overflow-y-auto scroll-soft px-5 py-4 space-y-3">
                            {(detail?.messages || []).map((m, i) => {
                                if (m.role === "user") {
                                    return (
                                        <div key={i} className="flex justify-start">
                                            <div className="max-w-[75%] bg-white border border-stone-200 rounded-2xl rounded-bl-sm px-3.5 py-2.5 text-sm text-stone-800 shadow-card">
                                                <div className="text-[10px] uppercase tracking-wide text-stone-400 mb-0.5">Guest</div>
                                                {m.attachment ? <ConsoleAttachment attachment={m.attachment} /> : m.text}
                                            </div>
                                        </div>
                                    );
                                }
                                if (m.role === "agent") {
                                    return (
                                        <div key={i} className="flex justify-end">
                                            <div className="max-w-[75%] bg-sage-600 text-white rounded-2xl rounded-br-sm px-3.5 py-2.5 text-sm shadow-card">
                                                <div className="text-[10px] uppercase tracking-wide text-white/70 mb-0.5">{m.agent_name || "You"}</div>
                                                {m.attachment ? <ConsoleAttachment attachment={m.attachment} light /> : m.text}
                                            </div>
                                        </div>
                                    );
                                }
                                if (m.role === "system") {
                                    return (
                                        <div key={i} className="flex justify-center">
                                            <div className="text-[11px] text-stone-500 bg-stone-100 rounded-full px-3 py-1">{m.text}</div>
                                        </div>
                                    );
                                }
                                return (
                                    <div key={i} className="flex justify-start">
                                        <div className="max-w-[75%] bg-orchid-700/5 border border-orchid-700/15 rounded-2xl rounded-bl-sm px-3.5 py-2.5 text-sm text-stone-700">
                                            <div className="text-[10px] uppercase tracking-wide text-orchid-700/70 mb-0.5">Priya (AI)</div>
                                            <div className="bot-text" dangerouslySetInnerHTML={{ __html: renderMarkdown(m.text) }} />
                                        </div>
                                    </div>
                                );
                            })}
                            <div ref={detailEndRef} />
                        </div>

                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                sendMsg();
                            }}
                            className="border-t border-stone-200 bg-white p-3 relative"
                        >
                            {showCanned && (
                                <div
                                    className="absolute bottom-full left-3 mb-2 w-80 max-h-72 overflow-y-auto scroll-soft bg-white border border-stone-200 rounded-xl shadow-lift p-2 z-20"
                                    data-testid="canned-list"
                                >
                                    <div className="text-[10px] uppercase tracking-wider text-stone-400 px-2 py-1">
                                        One-tap replies
                                    </div>
                                    {canned.length === 0 ? (
                                        <div className="text-xs text-stone-400 px-2 py-3">
                                            No saved replies. Add them in Settings → Canned Replies.
                                        </div>
                                    ) : (
                                        canned.map((c) => (
                                            <button
                                                key={c.id}
                                                type="button"
                                                onClick={() => insertCanned(c.text)}
                                                data-testid={`canned-${c.id}`}
                                                className="w-full text-left px-2.5 py-2 rounded-lg hover:bg-orchid-700/5 transition-colors"
                                            >
                                                <div className="text-xs font-semibold text-orchid-700">{c.title}</div>
                                                <div className="text-[11px] text-stone-500 line-clamp-2">{c.text}</div>
                                            </button>
                                        ))
                                    )}
                                </div>
                            )}
                            {uploading && (
                                <div className="text-[11px] text-stone-500 mb-1.5" data-testid="live-upload-progress">
                                    Uploading… {uploadPct}%
                                </div>
                            )}
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => setShowCanned((s) => !s)}
                                    disabled={!canType}
                                    data-testid="canned-toggle"
                                    title="Insert a saved reply"
                                    className="shrink-0 w-10 flex items-center justify-center rounded-full border border-stone-200 text-orchid-700 hover:bg-orchid-700/5 disabled:opacity-40"
                                >
                                    <Zap size={16} />
                                </button>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".jpg,.jpeg,.png,.webp,.pdf,.doc,.docx"
                                    className="hidden"
                                    data-testid="live-attach-input"
                                    onChange={(e) => uploadAgentFile(e.target.files?.[0])}
                                />
                                <button
                                    type="button"
                                    onClick={onPickAgentFile}
                                    disabled={!canType || uploading}
                                    data-testid="live-attach-btn"
                                    title="Attach a photo or document"
                                    className="shrink-0 w-10 flex items-center justify-center rounded-full border border-stone-200 text-orchid-700 hover:bg-orchid-700/5 disabled:opacity-40"
                                >
                                    {uploading ? <Loader2 size={16} className="animate-spin" /> : <Paperclip size={16} />}
                                </button>
                                <input
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    disabled={!canType}
                                    placeholder={canType ? "Type your reply to the guest…" : "This chat has ended"}
                                    data-testid="live-reply-input"
                                    className="flex-1 px-3.5 py-2 text-sm rounded-full border border-stone-200 focus:outline-none focus:border-orchid-700 focus:ring-2 focus:ring-orchid-700/15 disabled:bg-stone-50"
                                />
                                <button
                                    type="submit"
                                    disabled={!canType || sending || !input.trim()}
                                    data-testid="live-reply-send"
                                    className="bg-orchid-700 hover:bg-orchid-600 text-white px-4 rounded-full transition-colors disabled:opacity-50 flex items-center gap-1.5 text-sm font-medium"
                                >
                                    {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                                    Send
                                </button>
                            </div>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
}
